import React, { useState } from 'react';

import { formatMoney, snakeizeKeys, toMoney, useAutoNavigate } from '@betterment/js-runtime';
import { Box, Button, DropdownMenu, IconButton, Text, Tooltip } from '@betterment/samba-react';
import qs from 'query-string';

import { useAccount } from './AccountProvider';
import { useCustomer } from './CustomerProvider';
import LockedAccountModal from './LockedAccountModal';

import { trackClick } from '../../features/track';
import { locationAssign } from '../../Shared/path-helpers';
import { urlBuilder } from '../../Shared/urlBuilder';
import { Account } from '../data';

interface Props
  extends Pick<
    Account,
    'availableBalanceCents' | 'cumulativeReimbursedAtmFeesCents' | 'id' | 'cumulativeEarnedCashbackRewardsCents'
  > {
  hasMultipleBettermentAccounts: boolean;
}

const Balance: React.FC<React.PropsWithChildren<Props>> = ({
  availableBalanceCents,
  cumulativeReimbursedAtmFeesCents,
  cumulativeEarnedCashbackRewardsCents,
  id,
  hasMultipleBettermentAccounts,
}) => {
  const navigateRouter = useAutoNavigate();
  const { customer } = useCustomer();
  const { account } = useAccount();
  const [shouldShowLockedModal, setShouldShowLockedModal] = useState(false);

  const closeHandler = () => {
    setShouldShowLockedModal(false);
  };

  const isAccountOrCustomerLocked = customer.isLocked || account.isLocked;

  const availableBalance = formatMoney(toMoney(availableBalanceCents));
  const cumulativeReimbursedAtmFees = formatMoney(toMoney(cumulativeReimbursedAtmFeesCents));

  const buildTransferPath = () => {
    const basePath = urlBuilder.showTransferSetup.absolute();
    const params = {
      sourceAccountType: 'betterment_checking',
      sourceAccountId: id,
      returnTo: urlBuilder.getAppChecking.absolute(),
    };
    const query = qs.stringify(snakeizeKeys(params) as {});
    return `${basePath}?${query}`;
  };

  const handleTransferClick = () => {
    if (isAccountOrCustomerLocked) {
      setShouldShowLockedModal(true);
    } else {
      trackClick({
        name: 'TransferChecking',
        module: 'CheckingSummary',
      });
      locationAssign(buildTransferPath());
    }
  };

  const handleWithdrawClick = () => {
    if (isAccountOrCustomerLocked) {
      setShouldShowLockedModal(true);
    } else {
      trackClick({
        name: 'WithdrawChecking',
        module: 'CheckingSummary',
      });
      void navigateRouter(urlBuilder.getCheckingWithdrawalSetup.absolute({ accountId: id }));
    }
  };

  const doWithdrawal = () => {
    trackClick({
      name: 'WithdrawChecking',
      module: 'CheckingSummary',
    });
    void navigateRouter(urlBuilder.getCheckingWithdrawalSetup.absolute({ accountId: id }));
  };

  const handleDepositClick = () => {
    if (isAccountOrCustomerLocked) {
      setShouldShowLockedModal(true);
    } else {
      trackClick({
        name: 'DepositChecking',
        module: 'CheckingAccountInfo',
      });
      void navigateRouter(urlBuilder.getAppCheckingAccountDepositSetup.absolute({ accountId: id }));
    }
  };

  return (
    <Box css={{ bg: 'background.surface' }}>
      <Box
        css={{
          pt: ['space.200', 'space.200', 'space.300'],
          px: 'space.0',
          pb: ['space.200', 'space.200', 'space.400'],
        }}
      >
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <Box css={{ display: 'flex', flexWrap: ['wrap', null, 'nowrap'], justifyContent: 'flex-start' }}>
            <Box css={{ mr: ['space.600', 'space.800'], mb: ['space.200', 'space.200', 'space.100'] }}>
              <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box
                  aria-hidden={true}
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: ['space.50', 'space.100'],
                    minWidth: 'container.small',
                  }}
                >
                  <Text variant="text50">Available balance</Text>
                  <Tooltip>
                    <Tooltip.Trigger asChild={true}>
                      <IconButton label="Available balance info" name="QuestionMarkCircle" spacing="compact" />
                    </Tooltip.Trigger>
                    <Tooltip.Content>
                      Available balance is money that is ready to use. Withdrawals to your linked bank account and
                      immediately deducted. Pending deposits will show up in your balance after they have completed.
                    </Tooltip.Content>
                  </Tooltip>
                </Box>
                <Text
                  aria-label="Available balance"
                  aria-describedby="available_balance"
                  css={{ fontSize: ['fontSize.300', 'fontSize.400'], color: 'text' }}
                >
                  {availableBalance}
                </Text>
              </Box>
            </Box>
            <Box
              css={{
                width: 'space.50',
                mr: ['space.600', 'space.800'],
                mb: ['space.200', 'space.200', 'space.100'],
              }}
            >
              <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text
                  aria-hidden={true}
                  css={{ mb: ['space.50', 'space.100'], lineHeight: 'lineHeight.300' }}
                  variant="text50"
                >
                  ATM fees reimbursed
                </Text>
                <Text
                  aria-label="ATM fees reimbursed"
                  css={{ fontSize: ['fontSize.300', 'fontSize.400'], color: 'text' }}
                >
                  {cumulativeReimbursedAtmFees}
                </Text>
              </Box>
            </Box>
          </Box>
          <Box css={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box css={{ minWidth: ['50%', '50%', '25%'], pt: 'space.150' }}>
              <Box css={{ display: ['grid', 'flex'], gridTemplateColumns: '1fr 1fr', columnGap: 'space.200' }}>
                {!hasMultipleBettermentAccounts ? (
                  <Button variant="solidSecondary" onClick={doWithdrawal} aria-label="Withdraw">
                    Withdraw
                  </Button>
                ) : (
                  <DropdownMenu modal={false}>
                    <DropdownMenu.Trigger asChild={true}>
                      <Button variant="solidSecondary" aria-label="Transfer">
                        Transfer
                        <Button.Icon name="CaretDown" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="start">
                      <DropdownMenu.Item onSelect={handleTransferClick}>Transfer within Betterment</DropdownMenu.Item>
                      <DropdownMenu.Item onSelect={handleWithdrawClick}>Withdraw</DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu>
                )}
                <Button css={{ my: 'space.0' }} onClick={handleDepositClick} aria-label="Deposit">
                  Deposit
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {shouldShowLockedModal && <LockedAccountModal closeHandler={closeHandler} />}
    </Box>
  );
};

export default Balance;
